import { motion } from "framer-motion";

const CASES = [
  {
    n: "01",
    tag: "Code Blue",
    title: "Adult cardiac arrest, V-fib refractory to two shocks.",
    snippet:
      "Continue CPR. Epinephrine 1 mg IV q3–5 min. Amiodarone 300 mg IV bolus, may repeat 150 mg. Check H's & T's.",
  },
  {
    n: "02",
    tag: "RSI",
    title: "Rapid sequence intubation, weight 72 kg.",
    snippet:
      "Etomidate 0.3 mg/kg (~22 mg) + Rocuronium 1.2 mg/kg (~85 mg). Pre-oxygenate. Apneic oxygenation 15 L NC.",
  },
  {
    n: "03",
    tag: "Vasoactive",
    title: "Norepinephrine drip, central line, MAP < 65.",
    snippet:
      "Start 0.05 mcg/kg/min. Titrate q5 min by 0.02–0.05. Cap typically 1 mcg/kg/min. Add vasopressin at 0.5 mcg/kg/min.",
  },
  {
    n: "04",
    tag: "DKA",
    title: "DKA, glucose 612, K+ 3.2, bicarb 10.",
    snippet:
      "Hold insulin until K+ ≥ 3.3. Start 0.9% NS bolus 15 mL/kg. Replace K+ 20–40 mEq/L in fluids. Recheck BMP q2h.",
  },
];

export default function UseCases() {
  return (
    <section
      id="use-cases"
      data-testid="use-cases-section"
      className="relative py-28 md:py-36 bg-[#070707] border-t border-white/5"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-7">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-4">
              § 03 · in the trenches
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.95]">
              The questions
              <br />
              <span className="italic font-light">you actually</span> ask.
            </h2>
          </div>
          <p className="lg:col-span-5 text-base text-white/55 leading-relaxed self-end">
            Real scenarios. Real protocols. Real answers — not a 40-page PDF
            you'll never scroll through during a code.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {CASES.map((c, i) => (
            <motion.div
              key={c.n}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="group relative bg-[#050505] p-8 md:p-10 hover:bg-black transition-colors"
              data-testid={`use-case-${c.n}`}
            >
              <div className="flex items-start justify-between gap-6 mb-6">
                <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-white/35">
                  scenario {c.n}
                </span>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF2A2A] border border-[#FF2A2A]/50 px-2 py-1">
                  {c.tag}
                </span>
              </div>
              <h3 className="font-display text-xl md:text-2xl font-extrabold leading-tight tracking-tight mb-5">
                &quot;{c.title}&quot;
              </h3>
              <div className="border-l-2 border-[#FF2A2A] pl-4 py-1">
                <p className="text-sm text-white/65 leading-relaxed font-mono">
                  {c.snippet}
                </p>
              </div>
              <div className="mt-5 pt-4 border-t border-white/10 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FF2A2A]" />
                <span className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#FF2A2A]">
                  Sample · not medical advice · verify before use
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.22em] text-white/35 text-center">
          Illustrative sample outputs · not medical advice · always verify against current institutional protocol before use
        </p>
      </div>
    </section>
  );
}
